<template>
  <div class="page-features">
    <landing-header />

    <main>
      <section class="hero-section">
        <div class="max-w-7xl mx-auto px-4 text-center">
          <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Une plateforme complète pour <br />
            <span class="gradient-text">votre excellence opérationnelle</span>
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Découvrez comment Termoflow structure chaque aspect de votre métier, de l'intervention terrain jusqu'au pilotage financier de votre entreprise.
          </p>
          
          <div class="flex flex-wrap justify-center gap-3">
            <a v-for="group in featureGroups" :key="group.id" :href="'#' + group.id"
               class="nav-anchor">
              {{ group.title }}
            </a>
          </div>
        </div>
      </section>

      <div v-for="(group, index) in featureGroups" :key="group.id" :id="group.id" 
           class="feature-group-section" :class="{'bg-gray-50': index % 2 !== 0}">
        <div class="max-w-7xl mx-auto px-4">
          
          <div class="flex flex-col lg:flex-row items-center gap-16" :class="{'lg:flex-row-reverse': index % 2 !== 0}">
            
            <div class="lg:w-1/2">
              <div class="icon-badge">
                <i :class="group.icon"></i>
              </div>
              <h2 class="text-3xl font-extrabold text-gray-900 mb-8">{{ group.title }}</h2>
              
              <div class="space-y-12">
                <div v-for="feature in group.features" :key="feature.name" class="feature-item">
                  <h3 class="text-xl font-bold text-gray-900 mb-2">{{ feature.name }}</h3>
                  <p class="text-gray-600 leading-relaxed mb-4">{{ feature.description }}</p>
                  
                  <ul v-if="feature.details" class="detail-grid">
                    <li v-for="detail in feature.details" :key="detail" class="detail-pill">
                      <i class="fa-solid fa-circle-check"></i>
                      {{ detail }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="lg:w-1/2 w-full">
              <div class="mockup-container group">
                <div class="mockup-glow"></div>
                <div class="mockup-content">
                  <template v-if="group.img">
                    <img :src="group.img" :alt="'Aperçu ' + group.title" class="mockup-image" />
                  </template>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <landing-roi />
      <landing-avis />

    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'FeaturesPage',
  components: {
    LandingHeader,
    LandingAvis,
    'landing-roi': LandingROI,
    LandingFooter,
  },
  data() {
    return {
      featureGroups: [
        {
          id: 'terrain',
          title: 'Opérations & Terrain',
          icon: 'fa-solid fa-helmet-safety',
          img: 'https://storage.victoranw.fr/file/DX5lh1ldU1',
          features: [
            {
              name: 'Suivi des interventions',
              description: 'Bénéficiez d’une vision complète et structurée de votre activité opérationnelle en temps réel.',
              details: ['Statuts personnalisables', 'Affectation intelligente', 'Historique complet par appareil']
            },
            {
              name: 'Application mobile métier',
              description: 'Donnez à vos équipes un outil réellement adapté aux contraintes terrain, même sans connexion.',
              details: ['Mode offline complet', 'Accès documentation technique', 'Signature client sur tablette']
            },
            {
              name: 'Appareils & documentations',
              description: 'Accédez à une grande base technique du marché directement sur le terrain.',
              details: ['Schémas et notices', '72 000 appareils référencés', 'Mises à jour automatiques']
            }
          ]
        },
        {
          id: 'reglementaire',
          title: 'Conformité & Réglementation',
          icon: 'fa-solid fa-file-shield',
          img: 'https://storage.victoranw.fr/file/w7QsBjz5s2',
          features: [
            {
              name: 'CERFA & conformité',
              description: 'Transformez une contrainte réglementaire en processus maîtrisé et sans erreur.',
              details: ['Génération auto de CERFA', 'Données pré-remplies', 'Archivage sécurisé 5 ans']
            },
            {
              name: 'Bouteilles de gaz & fluides',
              description: 'Pilotez vos mouvements de fluides avec une précision chirurgicale.',
              details: ['Suivi des charges/récupérations', 'Registre de fluides auto', 'Gestion des stocks bouteilles']
            },
            {
              name: 'Détecteurs de gaz',
              description: 'Ne ratez plus aucune date d\'étalonnage pour vos équipements de sécurité.',
            }
          ]
        },
        {
          id: 'logistique',
          title: 'Logistique & Planning',
          icon: 'fa-solid fa-truck-ramp-box',
          img: 'https://storage.victoranw.fr/file/ng6f5PxDrg',
          features: [
            {
              name: 'Planification & tournées',
              description: 'Organisez vos journées avec méthode pour maximiser le temps facturable.',
              details: ['Calcul d\'itinéraires optimisés', 'Réduction des km parcourus', 'Vue carte techniciens']
            },
            {
              name: 'Gestion du stock',
              description: 'Maîtrisez votre matériel, du dépôt jusqu\'au fond du camion.',
              details: ['Stock camion en temps réel', 'Alertes de seuil critique', 'Inventaires simplifiés']
            }
          ]
        },
        {
          id: 'clientele',
          title: 'Gestion Client & Croissance',
          icon: 'fa-solid fa-chart-line',
          img: 'https://storage.victoranw.fr/file/YUQN6aQN7Y',
          features: [
            {
              name: 'Contrats d’entretien',
              description: 'Sécurisez votre récurrence en automatisant la gestion de vos parcs sous contrat.',
              details: ['Relances automatiques', 'Rentabilité par contrat']
            },
            {
              name: 'Portail client',
              description: 'Offrez une transparence totale et réduisez vos appels administratifs.',
              details: ['Téléchargement CERFA autonome', 'Prise de RDV en ligne', 'Historique photos']
            },
            {
              name: 'Communication automatisée',
              description: 'Restez proche de vos clients sans effort manuel.',
              details: ['SMS de rappel RDV', 'Avis Google post-intervention']
            }
          ]
        },
        {
          id: 'pilotage',
          title: 'Analyse & Pilotage',
          icon: 'fa-solid fa-microchip',
          img: 'https://storage.victoranw.fr/file/y2qJIvRTUf',
          features: [
            {
              name: 'Statistiques & pilotage',
              description: 'Pilotez votre entreprise avec des chiffres, pas avec des intuitions.',
              details: ['KPIs de productivité', 'Analyse marge par technicien']
            },
            {
              name: 'Exports & reporting',
              description: 'Extrayez vos données en un clic pour votre comptabilité ou vos analyses.',
            },
            {
              name: 'Intégrations',
              description: 'Termoflow se connecte à vos logiciels de facturation et comptabilité existants.',
            }
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
/* --- BASE & TYPO --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* --- HERO --- */
.hero-section {
  padding: 10rem 1rem 5rem;
  background: radial-gradient(circle at top right, #fff7ed, transparent 40%),
              radial-gradient(circle at bottom left, #fef2f2, transparent 40%);
}

.nav-anchor {
  display: inline-block;
  padding: 0.6rem 1.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 800;
  color: #4b5563;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  margin: 0.25rem;
}

.nav-anchor:hover {
  border-color: #f97316;
  color: #f97316;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(249, 115, 22, 0.1);
}

/* --- SECTIONS --- */
.feature-group-section {
  padding: 7rem 0;
  border-bottom: 1px solid #f3f4f6;
  scroll-margin-top: 5rem;
}

.icon-badge {
  width: 3.5rem;
  height: 3.5rem;
  background: linear-gradient(135deg, #f97316, #ef4444);
  color: white;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 10px 20px rgba(239, 68, 68, 0.2);
  margin-bottom: 2rem;
}

.feature-item {
  position: relative;
  padding-left: 2.5rem;
}

.feature-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.5rem;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #f97316, transparent);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  padding: 0;
  margin-top: 1rem;
}

.detail-pill {
  list-style: none;
  font-size: 0.85rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.detail-pill i {
  color: #f97316;
  font-size: 0.7rem;
}

/* --- MOCKUPS --- */
.mockup-container {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.mockup-glow {
  position: absolute;
  inset: -10px;
  background: linear-gradient(to right, #f97316, #ef4444);
  border-radius: 2.5rem;
  filter: blur(30px);
  opacity: 0.08;
}

.mockup-content {
  position: relative;
  background: white;
  border: 1px solid #f3f4f6;
  border-radius: 2rem;
  aspect-ratio: 4 / 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.1);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
}

.mockup-container:hover .mockup-content {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 40px 80px -15px rgba(249, 115, 22, 0.15);
}

.mockup-icon {
  font-size: 4rem;
  color: #f3f4f6;
  margin-bottom: 1.5rem;
  transition: color 0.4s ease;
}

.mockup-container:hover .mockup-icon {
  color: rgba(249, 115, 22, 0.1);
}

.mockup-label {
  color: #9ca3af;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.7rem;
}

.mockup-sublabel {
  color: #d1d5db;
  font-size: 0.65rem;
  margin-top: 0.5rem;
}

/* --- SMOOTHING --- */
html {
  scroll-behavior: smooth;
}

@media (max-width: 1024px) {
  .hero-section { padding-top: 8rem; }
  .feature-group-section { padding: 4rem 0; }
  .mockup-container { margin-top: 3rem; }
}
</style>